// HomePage — stillwatersacademy.com front door.
// One brand truth, five doors, real proof before price. Returning users
// get their own door in one click. Ava's panel is the real demo child
// (demo_child_showcase, anon-granted) — same data the sponsor funnel shows.
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  GraduationCap, Home, Crown, Building2, Church, Heart, ArrowRight,
  BookOpen, Sparkles, ChevronRight, User, Globe,
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { getDashboardPath } from '@/utils/getDashboardPath'
import SEO from '@/components/layout/SEO'
import LanguagePicker from '@/components/ui/LanguagePicker'
import SevenDoors from '@/components/sections/SevenDoors'

// ── Tokens: the platform dark family ─────────────────────────────────────────
const M = '#06060c'
const D2 = '#0a0a14'
const CARD = '#0d0d15'
const BLUE = '#0ea5e9'
const G = '#d4a843'
const BR = 'rgba(212,168,67,0.15)'
const BL = 'rgba(14,165,233,0.15)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const MU2 = 'rgba(255,255,255,0.35)'
const serif = "'Playfair Display', Georgia, serif"
const sans = "'Inter', system-ui, sans-serif"

export default function HomePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, userProfile } = useAuth()

  const [demo, setDemo] = useState<any | null>(null)
  const [totalCourses, setTotalCourses] = useState<number>(231)

  // ── Live counts across all streams: Standard, University, Church/Bible, Community, Elite ──
  useEffect(() => {
    Promise.all([
      // Master curriculum hub (standard): 114
      supabase.from('classes').select('id', { count: 'exact', head: true })
        .eq('is_active', true)
        .eq('facility_id', '00000000-0000-0000-0000-000000000001'),
      // University degrees: 45
      supabase.from('classes').select('id', { count: 'exact', head: true })
        .eq('is_active', true)
        .eq('facility_id', '00000000-0000-0000-0000-000000000002')
        .is('curriculum_track', null),
      // Church & Bible journey: 31
      supabase.from('classes').select('id', { count: 'exact', head: true })
        .eq('is_active', true)
        .or('in_church_catalog.eq.true,curriculum_track.eq.faith'),
      // Community course library: 11
      supabase.from('community_course_library' as any).select('id', { count: 'exact', head: true }),
    ]).then(([std, uni, ch, el]: any) => {
      const standardCount = (typeof std?.count === 'number' && std.count > 0) ? std.count : 114
      const universityCount = (typeof uni?.count === 'number' && uni.count > 0) ? uni.count : 45
      const churchCount = (typeof ch?.count === 'number' && ch.count > 0) ? ch.count : 31
      const communityCount = (typeof el?.count === 'number' && el.count > 0) ? el.count : 11
      const eliteCount = 30
      setTotalCourses(standardCount + universityCount + churchCount + communityCount + eliteCount)
    }).catch(() => {
      setTotalCourses(114 + 45 + 31 + 11 + 30) // 231
    })
  }, [])

  // ── Ava — the real sample learner, public read ──
  useEffect(() => {
    Promise.resolve(
      supabase.from('demo_child_showcase' as any).select('*').limit(1).maybeSingle()
    ).then(({ data }: any) => { if (data) setDemo(data) })
      .catch(() => {})
  }, [])

  const returningPath = user ? getDashboardPath(userProfile, (user as any)?.user_metadata) : null

  // Ava's real quiz history (the view returns logs newest-first)
  const quizLogs = (Array.isArray(demo?.recent_logs) ? demo.recent_logs : [])
    .filter((l: any) => l?.metadata?.phase === 'quiz_result')
    .slice(0, 5)

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: sans }}>
      <SEO
        title="Still Waters Academy | AI Education for Families, Schools, Churches, and Every Child"
        description="World-class AI education for every kind of learner. 230+ courses, personal AI tutors, real transcripts, and sponsored seats for children in need. Find your path in under a minute."
      />

      {/* ═══ NAV ═══ */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(6,6,12,0.92)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${BR}`, padding: '12px 16px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexShrink: 1 }}>
            <div style={{ width: 32, height: 32, flexShrink: 0 }}>
              <img src="/icons/blue-sage-logo.png" alt="Still Waters" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span style={{ fontFamily: serif, fontWeight: 700, fontSize: 'clamp(14px, 4vw, 18px)', color: TX, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Still Waters <span style={{ color: BLUE }}>Academy</span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <LanguagePicker />
            {user ? (
              <button onClick={() => navigate(returningPath || '/learn')}
                style={{ background: G, color: M, padding: '7px 14px', borderRadius: 999, fontSize: 12, fontWeight: 800, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t('home.nav.myDashboard', 'Dashboard')} →
              </button>
            ) : (
              <button onClick={() => navigate('/auth/login')}
                style={{ background: 'none', border: `1px solid ${BR}`, color: TX, padding: '7px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t('home.nav.signIn', 'Sign In')}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* ═══ HERO — one truth, one action ═══ */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '110px 24px 70px', textAlign: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.07, pointerEvents: 'none', background: `radial-gradient(ellipse at 50% 0%, ${BLUE} 0%, transparent 55%)` }} />
        <div style={{ maxWidth: 860, margin: '0 auto', position: 'relative' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 999, background: BL, border: `1px solid rgba(14,165,233,0.3)`, color: BLUE, fontSize: 11, fontWeight: 800, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 30 }}>
            <Sparkles size={12} /> {t('home.hero.badge', 'Teachers who never sleep')}
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            style={{ fontFamily: serif, fontSize: 'clamp(2.6rem, 6.5vw, 4.8rem)', fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em', margin: '0 0 24px', color: TX }}>
            {t('home.hero.line1', 'A world-class education')}<br />
            <span style={{ color: BLUE }}>{t('home.hero.line2', 'for every kind of learner.')}</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
            style={{ color: MU, fontSize: 'clamp(16px, 2.2vw, 20px)', lineHeight: 1.7, maxWidth: 640, margin: '0 auto 14px' }}>
            {t('home.hero.sub', {
              count: totalCourses,
              defaultValue: `One platform: ${totalCourses} courses taught one-on-one by AI tutors who adapt to every learner, real transcripts, and a sponsored seat for the child who cannot pay.`,
            })}
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.24 }}
            style={{ color: G, fontSize: 15, fontWeight: 700, margin: '0 auto 40px' }}>
            {t('home.hero.sub2', 'From primary school to university, from your family to a village that has no school.')}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            {user ? (
              <button onClick={() => navigate(returningPath || '/learn')}
                style={{ padding: '20px 52px', borderRadius: 999, background: `linear-gradient(135deg, ${G}, #b8912e)`, color: M, fontWeight: 800, fontSize: 18, border: 'none', cursor: 'pointer', boxShadow: `0 8px 40px ${G}55` }}>
                {t('home.hero.goDashboard', 'Go to My Dashboard')} →
              </button>
            ) : (
              <button onClick={() => document.getElementById('paths')?.scrollIntoView({ behavior: 'smooth' })}
                style={{ padding: '20px 52px', borderRadius: 999, background: `linear-gradient(135deg, ${G}, #b8912e)`, color: M, fontWeight: 800, fontSize: 18, border: 'none', cursor: 'pointer', boxShadow: `0 8px 40px ${G}55` }}>
                {t('home.hero.cta', 'Find Your Path')} ↓
              </button>
            )}
            <button onClick={() => navigate('/prep')}
              style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontWeight: 700, fontSize: 14, padding: 0, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
              {t('home.hero.freeLesson', 'Or try a full lesson free, no account needed')}
            </button>
          </motion.div>

          {/* Live count strip */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
            style={{ marginTop: 56, paddingTop: 28, borderTop: `1px solid ${BR}`, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '32px 48px' }}>
            {[
              { v: `${totalCourses}+`, l: t('home.stats.courses', 'Courses') },
              { v: '45', l: t('home.stats.degrees', 'University Degrees') },
              { v: '24/7', l: t('home.stats.tutor', 'Personal AI Tutor') },
              { v: '100%', l: t('home.stats.transcripts', 'Printable Transcripts') },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: serif, fontSize: 32, fontWeight: 800, color: G }}>{s.v}</div>
                <div style={{ fontSize: 11, color: MU2, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginTop: 4 }}>{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ THE CORRIDOR — seven doors, one school ═══ */}
      <SevenDoors />

      {/* ═══ PROOF — Ava's real record ═══ */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 48, alignItems: 'center' }}>
          <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <p style={{ fontSize: 12, color: BLUE, fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', margin: '0 0 12px' }}>
              {t('home.proof.eyebrow', 'This is what learning looks like here')}
            </p>
            <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', fontWeight: 700, lineHeight: 1.15, margin: '0 0 18px', color: TX }}>
              {t('home.proof.title1', 'Not a demo video.')}<br />
              <span style={{ color: BLUE }}>{t('home.proof.title2', 'A real learner, really learning.')}</span>
            </h2>
            <p style={{ color: MU, fontSize: 15.5, lineHeight: 1.7, margin: '0 0 20px' }}>
              {t('home.proof.body', 'This is Ava, age 12, studying Presence & Command. Every number beside her is live from her actual lessons: she failed a quiz, went back, and passed it. That retry is the product. Our AI tutors do not move a child on until they have genuinely learned, and the record proves it.')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                t('home.proof.f1', 'Mastery-based: 80% to pass, unlimited dignified retakes'),
                t('home.proof.f2', 'A written profile of every learner: strengths and growth edges'),
                t('home.proof.f3', 'Printable records a school or employer accepts'),
              ].map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: TX, fontSize: 14, fontWeight: 600 }}>
                  <span style={{ color: G, fontWeight: 900 }}>✓</span> {f}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 26 }}>
              <Link to="/sponsor/catalog" style={{ color: G, fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>
                {t('home.proof.browse', 'Browse every course a learner can take')} →
              </Link>
            </div>
          </motion.div>

          {/* Ava panel — live data */}
          <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.12 }}>
            <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 24, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 900, color: G, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                    {t('home.proof.sampleLearner', 'Sample Learner · Real Data')}
                  </div>
                  <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, marginTop: 4, color: TX }}>
                    {demo
                      ? t('home.proof.childAge', {
                          name: demo.child_name,
                          age: demo.child_age,
                          defaultValue: `${demo.child_name}, age ${demo.child_age}`,
                        })
                      : t('home.proof.ava', 'Ava, age 12')}
                  </div>
                </div>
                <span style={{ padding: '4px 12px', borderRadius: 999, background: 'rgba(74,222,128,0.15)', color: '#4ade80', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>
                  {t('home.proof.active', 'Learning now')}
                </span>
              </div>

              {[
                { l: t('home.proof.lessons', 'Lessons completed'), v: demo ? String(quizLogs.length) : '5', w: 38 },
                { l: t('home.proof.mastery', 'Mastery score'), v: demo ? `${Math.round(Number(demo?.enrollments?.[0]?.mastery_score ?? 80))}%` : '80%', w: 80 },
              ].map((m, i) => (
                <div key={i} style={{ marginBottom: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: MU, fontSize: 12, fontWeight: 600 }}>{m.l}</span>
                    <span style={{ color: G, fontSize: 14, fontWeight: 800 }}>{m.v}</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}>
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${m.w}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 + i * 0.15 }}
                      style={{ height: '100%', background: `linear-gradient(90deg, ${G}, #f0d78c)`, borderRadius: 999 }} />
                  </div>
                </div>
              ))}

              {demo && quizLogs.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 10, fontWeight: 900, color: G, letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 8px' }}>
                    {t('home.proof.quizHistory', 'Actual quiz history')}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {quizLogs.map((log: any, i: number) => {
                      const m = log?.metadata || {}
                      const retry = Number(m.attempt ?? 1) > 1
                      const score = Number(m.score ?? 0)
                      return (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5, color: MU }}>
                          <span>U{log.unit_number} · L{log.lesson_number}{retry ? ` · ${t('home.proof.retry', 'retry')} ✓` : ''}</span>
                          <span style={{ fontWeight: 800, color: score >= 80 ? '#4ade80' : '#fbbf24' }}>{score}%</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {(() => {
                const p = demo?.global_academic_profile || {}
                const strengths = (p.strengths || []).slice(0, 2)
                const struggles = (p.struggles || []).slice(0, 1)
                if (!strengths.length && !struggles.length) return null
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                    {strengths.map((s: string) => (
                      <span key={s} style={{ fontSize: 10, color: '#4ade80', background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.25)', padding: '3px 9px', borderRadius: 999, fontWeight: 700 }}>{s}</span>
                    ))}
                    {struggles.map((s: string) => (
                      <span key={s} style={{ fontSize: 10, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', padding: '3px 9px', borderRadius: 999, fontWeight: 700 }}>{s}</span>
                    ))}
                  </div>
                )
              })()}

              <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 14, fontSize: 11, color: MU }}>
                {t('home.proof.footer', 'Live lesson log · nothing simulated')}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section style={{ padding: '70px 24px', background: D2 }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 700, margin: '0 0 44px', textAlign: 'center', color: TX }}>
            {t('home.how.title', 'Three steps. Then they are learning.')}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 20 }}>
            {[
              { n: '01', icon: '👤', t: t('home.how.s1', 'Tell us who is learning'), d: t('home.how.s1d', 'A child, a class, a congregation, or yourself. The tutor adapts vocabulary, pace, and depth to the human, not the course.') },
              { n: '02', icon: '📖', t: t('home.how.s2', 'Choose the courses'), d: t('home.how.s2d', 'Pick from the catalog or generate a custom one in minutes. Every lesson is taught one-on-one, in dialogue, in your language.') },
              { n: '03', icon: '📈', t: t('home.how.s3', 'Watch the graph go up'), d: t('home.how.s3d', 'Mastery scores, written feedback, strengths, growth edges, transcripts. Proof of learning, not just completion.') },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 22, padding: '30px 26px' }}>
                <div style={{ fontSize: 36, marginBottom: 14 }}>{s.icon}</div>
                <div style={{ color: G, fontSize: 11, fontWeight: 900, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}>{t('home.how.step', 'Step')} {s.n}</div>
                <h3 style={{ fontFamily: serif, fontSize: 19, fontWeight: 700, margin: '0 0 10px', color: TX }}>{s.t}</h3>
                <p style={{ color: MU, fontSize: 13.5, lineHeight: 1.65, margin: 0 }}>{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CLOSING ═══ */}
      <section style={{ padding: '90px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.1, pointerEvents: 'none', background: `radial-gradient(ellipse at center, ${G} 0%, transparent 60%)` }} />
        <div style={{ maxWidth: 640, margin: '0 auto', position: 'relative' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2.2rem, 5vw, 3.4rem)', fontWeight: 700, lineHeight: 1.1, margin: '0 0 18px', color: TX }}>
            {t('home.close.title1', 'Somewhere, someone is ready')}<br />
            <span style={{ color: G }}>{t('home.close.title2', 'to start learning today.')}</span>
          </h2>
          <p style={{ color: MU, fontSize: 16, lineHeight: 1.7, margin: '0 0 36px' }}>
            {t('home.close.body', 'Your first lesson is free. Every door leads to the same engine, the same tutors, the same proof. Begin where you belong.')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <button onClick={() => document.getElementById('paths')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ padding: '18px 48px', borderRadius: 999, background: G, color: M, fontWeight: 800, fontSize: 17, border: 'none', cursor: 'pointer', boxShadow: `0 10px 50px ${G}44` }}>
              {t('home.close.cta', 'Find Your Path')}
            </button>
            <button onClick={() => navigate('/prep')}
              style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontWeight: 700, fontSize: 14, padding: 0, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
              {t('home.close.free', 'Try a free lesson first')}
            </button>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ borderTop: `1px solid ${BR}`, padding: '36px 24px', display: 'flex', justifyContent: 'center', gap: 28, flexWrap: 'wrap' }}>
        {[
          { l: t('home.footer.families', 'Families'), to: '/homeschool' },
          { l: t('home.footer.elite', 'Elite Academy'), to: '/elite' },
          { l: t('home.footer.schools', 'Schools'), to: '/schools' },
          { l: t('home.footer.church', 'Churches'), to: '/church' },
          { l: t('home.footer.university', 'University'), to: '/university' },
          { l: t('home.footer.sponsor', 'Sponsor'), to: '/sponsor' },
          { l: t('home.footer.privacy', 'Privacy'), to: '/privacy' },
          { l: t('home.footer.terms', 'Terms'), to: '/terms' },
        ].map(l => (
          <Link key={l.l} to={l.to} style={{ color: MU2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', textDecoration: 'none' }}>{l.l}</Link>
        ))}
      </footer>
    </div>
  )
}